import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { FileSpreadsheet, Loader2 } from 'lucide-react';
import { useStore } from '../../store';
import { getAgencyTimezone } from '../../utils/agencyTimezone';
import { formatDateTimeInZone, getTodayInPT } from '../../utils/dateUtils';
import {
  downloadCahpsExport,
  generateCahpsExport,
  listCahpsExports,
  previewCahpsExport,
  type CahpsExportListItem,
  type CahpsPreview,
} from '../../services/cahpsExports';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import CahpsSummaryPanel, { IncompleteMark } from './cahps/CahpsSummaryPanel';

const REPORTING_MONTHS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const MIN_REPORTING_YEAR = 2026;

function getReportingYears(): string[] {
  const currentYear = Number(getTodayInPT().split('-')[0]);
  const years: string[] = [];
  for (let year = currentYear; year >= MIN_REPORTING_YEAR; year--) {
    years.push(String(year));
  }
  return years;
}

function getDefaultSamplePeriod(): { month: string; year: string } {
  const [year, month] = getTodayInPT().split('-');
  return { month: String(Number(month)), year };
}

// Only an agency moving from another EMR gets a line; others see nothing.
function previewLine(preview: CahpsPreview): string | null {
  if (preview.reads_kinnser) {
    return "Generate will read your previous EMR's CAHPS files and combine them with Roger data";
  }
  if (preview.has_stored_files) {
    return `Generate will combine your previous EMR's CAHPS files from ${
      preview.stored_at
        ? formatDateTimeInZone(
            preview.stored_at,
            getAgencyTimezone(),
            undefined,
            'M/d/yyyy'
          )
        : 'earlier'
    } with Roger data`;
  }
  return null;
}

function isFuturePeriod(month: number, year: number): boolean {
  const [currentYear, currentMonth] = getTodayInPT().split('-').map(Number);
  return year > currentYear || (year === currentYear && month > currentMonth);
}

// The year shows only when the export was made in a different year from its month.
function exportRowLabel(item: CahpsExportListItem): string {
  const zone = getAgencyTimezone();
  const year = formatDateTimeInZone(item.created_at, zone, undefined, 'yyyy');
  const date = formatDateTimeInZone(
    item.created_at,
    zone,
    undefined,
    year === String(item.sample_year) ? 'M/d' : 'M/d/yyyy'
  );
  const by = item.generated_by ? ` by ${item.generated_by}` : '';
  return `Generated ${date}${by} · ${item.row_count} patients`;
}

interface MonthGroup {
  key: string;
  month: number;
  year: number;
  items: CahpsExportListItem[];
}

function groupByMonth(items: CahpsExportListItem[]): MonthGroup[] {
  const groups = new Map<string, MonthGroup>();
  for (const item of items) {
    const key = `${item.sample_year}-${item.sample_month}`;
    const group = groups.get(key) ?? {
      key,
      month: item.sample_month,
      year: item.sample_year,
      items: [],
    };
    group.items.push(item);
    groups.set(key, group);
  }
  for (const group of groups.values()) {
    group.items.sort((a, b) => b.created_at.localeCompare(a.created_at));
  }
  return [...groups.values()].sort(
    (a, b) => b.year - a.year || b.month - a.month
  );
}

const CahpsSection: React.FC = () => {
  const reportingYears = useMemo(() => getReportingYears(), []);
  const [selectedMonth, setSelectedMonth] = useState(
    () => getDefaultSamplePeriod().month
  );
  const [selectedYear, setSelectedYear] = useState(
    () => getDefaultSamplePeriod().year
  );
  const [exports, setExports] = useState<CahpsExportListItem[]>([]);
  const [isLoadingExports, setIsLoadingExports] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadingExportId, setDownloadingExportId] = useState<string | null>(
    null
  );
  const [preview, setPreview] = useState<CahpsPreview | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  // Bumped after Generate, which may have stored Kinnser's files for the month.
  const [previewVersion, setPreviewVersion] = useState(0);
  const [selected, setSelected] = useState<CahpsExportListItem | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const showError = useStore(state => state.showError);

  const loadExports = useCallback(async () => {
    setIsLoadingExports(true);
    try {
      const items = await listCahpsExports();
      setExports(items);
      // Keep the open summary; with nothing open, show the newest export.
      setSelected(current =>
        current
          ? (items.find(item => item.id === current.id) ?? current)
          : (items[0] ?? null)
      );
    } catch (error) {
      console.error('Failed to load CAHPS exports:', error);
      showError('Failed to load CAHPS export history.');
    } finally {
      setIsLoadingExports(false);
    }
  }, [showError]);

  useEffect(() => {
    void loadExports();
  }, [loadExports]);

  useEffect(() => {
    const sampleMonth = Number(selectedMonth);
    const sampleYear = Number(selectedYear);
    setPreview(null);
    setPreviewFailed(false);
    if (isFuturePeriod(sampleMonth, sampleYear)) {
      return;
    }
    let current = true;
    previewCahpsExport(sampleMonth, sampleYear)
      .then(result => {
        if (current) setPreview(result);
      })
      .catch((error: unknown) => {
        console.error('Failed to preview CAHPS export:', error);
        if (current) setPreviewFailed(true);
      });
    return () => {
      current = false;
    };
  }, [selectedMonth, selectedYear, previewVersion]);

  const availableMonths = useMemo(() => {
    const [currentYear, currentMonth] = getTodayInPT().split('-').map(Number);
    const year = Number(selectedYear);
    if (year < currentYear) {
      return REPORTING_MONTHS;
    }
    return REPORTING_MONTHS.filter(
      month => Number(month.value) <= currentMonth
    );
  }, [selectedYear]);

  const handleGenerate = async () => {
    const sampleMonth = Number(selectedMonth);
    const sampleYear = Number(selectedYear);
    if (!Number.isInteger(sampleYear) || sampleYear < MIN_REPORTING_YEAR) {
      showError('Select a valid reporting year.');
      return;
    }
    if (isFuturePeriod(sampleMonth, sampleYear)) {
      showError('Exports cannot be generated for a future reporting period.');
      return;
    }

    setIsGenerating(true);
    setRefusal(null);
    try {
      const item = await generateCahpsExport(sampleMonth, sampleYear);
      setSelected(item);
      setPreviewVersion(version => version + 1);
      await loadExports();
    } catch (error) {
      console.error('Failed to generate CAHPS export:', error);
      const status = getHttpStatus(error);
      // A 4xx is a Roger-side refusal with an actionable reason; it stays on the page.
      if (status !== undefined && status >= 400 && status < 500) {
        setSelected(null);
        setRefusal(
          getUserFacingApiMessage(error, 'The export could not be generated.')
        );
      } else {
        showError('Failed to generate HHCAHPS export.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async (item: CahpsExportListItem) => {
    setDownloadingExportId(item.id);
    try {
      await downloadCahpsExport(item.id, item.file_name);
    } catch (error) {
      console.error('Failed to download CAHPS export:', error);
      showError('Failed to download HHCAHPS export.');
    } finally {
      setDownloadingExportId(null);
    }
  };

  const formatPeriod = (month: number, year: number) => {
    const label = REPORTING_MONTHS.find(
      option => option.value === String(month)
    );
    return `${label?.label ?? month} ${year}`;
  };

  return (
    <div className='flex flex-col gap-4 md:h-full'>
      <section className='space-y-2 md:shrink-0'>
        <div className='flex flex-wrap items-end gap-4'>
          <div className='space-y-1.5'>
            <label
              htmlFor='cahps-reporting-month'
              className='text-sm font-medium'
            >
              Reporting month
            </label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger id='cahps-reporting-month' className='w-40'>
                <SelectValue placeholder='Select month' />
              </SelectTrigger>
              <SelectContent>
                {availableMonths.map(month => (
                  <SelectItem key={month.value} value={month.value}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-1.5'>
            <label
              htmlFor='cahps-reporting-year'
              className='text-sm font-medium'
            >
              Reporting year
            </label>
            <Select
              value={selectedYear}
              onValueChange={year => {
                setSelectedYear(year);
                const [currentYear, currentMonth] = getTodayInPT()
                  .split('-')
                  .map(Number);
                if (
                  Number(year) === currentYear &&
                  Number(selectedMonth) > currentMonth
                ) {
                  setSelectedMonth(String(currentMonth));
                }
              }}
            >
              <SelectTrigger id='cahps-reporting-year' className='w-32'>
                <SelectValue placeholder='Select year' />
              </SelectTrigger>
              <SelectContent>
                {reportingYears.map(year => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            type='button'
            disabled={
              isGenerating ||
              isFuturePeriod(Number(selectedMonth), Number(selectedYear))
            }
            onClick={handleGenerate}
          >
            {isGenerating ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <FileSpreadsheet className='mr-2 h-4 w-4' />
            )}
            Generate Export
          </Button>
          <div className='text-xs md:flex md:h-8 md:items-center'>
            {isGenerating && preview?.reads_kinnser ? (
              <p>Reading your previous EMR. This can take a few minutes.</p>
            ) : preview ? (
              <p className='text-muted-foreground'>{previewLine(preview)}</p>
            ) : previewFailed ? (
              <p className='text-muted-foreground'>
                Unable to check this month's data sources.
              </p>
            ) : null}
          </div>
        </div>
      </section>

      <div className='flex flex-col pt-2 md:min-h-0 md:flex-1'>
        <h3 className='pb-3 text-base font-semibold'>Previous exports</h3>
        <div className='flex flex-col gap-4 md:min-h-0 md:flex-1 md:flex-row'>
          <section
            aria-label='Saved exports'
            className='rounded-md border md:min-w-0 md:flex-1 md:basis-0 md:overflow-y-auto'
          >
            {isLoadingExports && exports.length === 0 ? (
              <p className='px-3 py-2 text-sm text-muted-foreground'>
                Loading saved exports...
              </p>
            ) : exports.length === 0 ? (
              <p className='px-3 py-2 text-sm text-muted-foreground'>
                No saved exports yet.
              </p>
            ) : (
              <div>
                {groupByMonth(exports).map(group => (
                  <div key={group.key}>
                    <h4 className='sticky top-0 border-b bg-muted px-4 py-2 text-sm font-medium'>
                      {formatPeriod(group.month, group.year)}
                    </h4>
                    <ul>
                      {group.items.map(item => {
                        const isSelected = !refusal && selected?.id === item.id;
                        return (
                          <li key={item.id} className='border-b'>
                            <button
                              type='button'
                              aria-current={isSelected ? 'true' : undefined}
                              onClick={() => {
                                setRefusal(null);
                                setSelected(item);
                              }}
                              className={`flex w-full items-center justify-between gap-2 py-3 pl-7 pr-4 text-left text-[13px] text-muted-foreground hover:bg-muted/30 ${
                                isSelected ? 'bg-muted/50' : ''
                              }`}
                            >
                              <span className='flex items-center gap-2'>
                                <FileSpreadsheet className='h-4 w-4 shrink-0' />
                                {exportRowLabel(item)}
                              </span>
                              <IncompleteMark complete={item.complete} />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className='min-w-0 flex-1 rounded-md border p-4 md:basis-0 md:overflow-y-auto'>
            {refusal ? (
              <section aria-label='Generate summary' className='space-y-2'>
                <Alert variant='destructive'>
                  <AlertTitle>No file was generated</AlertTitle>
                  <AlertDescription>{refusal}</AlertDescription>
                </Alert>
              </section>
            ) : selected ? (
              <CahpsSummaryPanel
                item={selected}
                periodLabel={formatPeriod(
                  selected.sample_month,
                  selected.sample_year
                )}
                isDownloading={downloadingExportId === selected.id}
                onDownload={() => void handleDownload(selected)}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CahpsSection;
